import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, type DocumentNode } from '@apollo/client';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons/faChevronDown';
import { faXmark } from '@fortawesome/pro-regular-svg-icons/faXmark';
import { faMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons/faMagnifyingGlass';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { cn } from '../lib/utils';
import { type DocumentElement as DocumentElementType } from '@eaco/forms-core';
import {
  useDocument,
  useElementValue,
  useIsEditor,
  useIsReadOnly,
} from '../DocumentView/DocumentContext';
import { unwrapEnvelope } from '@eaco/forms-core';
import {
  useFormFillContext,
  type ContextEntityKind,
} from '../DocumentView/FormFillContext';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from '../ui/popover';
import HiddenElementBanner from './HiddenElementBanner';
import ArchivedElementBanner from './ArchivedElementBanner';
import { FieldLabel, FieldSubLabel, FieldHelpText } from './FieldParts';

/**
 * Picker rows returned by the Entities-group data resolvers. Every
 * entity guarantees an `id` + `name`; everything else is per-entity
 * (address for properties, email for contacts, ref for jobs, …).
 */
export interface EntityPickerRow {
  id: string;
  name: string;
  [key: string]: unknown;
}

/** Per-pick snapshot persisted in the form values. */
export interface EntityRichEntry {
  id: string;
  name: string;
  [key: string]: unknown;
}

/**
 * Render hooks per entity: each picker passes in just the bits that
 * differ — query, default copy, icon, and small projection helpers.
 * The rest of the popover / chip list / pill is shared.
 */
export interface EntityPickerProps {
  element: DocumentElementType;
  /**
   * Apollo document for the entity's collection query. Typed as
   * `any` so a graphql 15.x consumer (apps/web) and a graphql 16.x
   * package can pass a DocumentNode without TS treating their
   * structurally-identical types as incompatible. Apollo's
   * `useQuery` accepts the raw document either way.
   */
  query: DocumentNode | any;
  /** Top-level field on the query response (e.g. "documentElementContacts"). */
  collectionField: string;
  /** Icon shown next to each row + chip — fa-pro-regular icon. */
  icon: IconDefinition;
  /** Entity accent colour for the picker badge (defaults to the form accent). */
  iconColour?: string;
  /** "avatar" shows the entity's initials in a circle instead of the icon. */
  variant?: 'icon' | 'avatar';
  defaults: {
    label: string;
    placeholder: string;
  };
  /** Subtitle text for a row (e.g. address, email, ref id). null hides it. */
  rowSubtitle?: (row: EntityPickerRow) => string | null;
  /** Small metadata badges shown under the subtitle (e.g. customer name + ref). */
  rowBadges?: (row: EntityPickerRow) => string[];
  /**
   * Project a fetched row into the persisted snapshot. Defaults to
   * `{id, name}`; override to add entity-specific keys.
   */
  rowToSnapshot?: (row: EntityPickerRow) => EntityRichEntry;
  /**
   * Per-entity form-context relationship spec. Drives the context
   * configForm toggles (autoFillFromContext / filterByXContext) at
   * runtime — the picker reads its element.data flags + the live
   * form-fill context and behaves accordingly.
   */
  contextSpec?: PickerContextSpec;
}

export interface PickerContextSpec {
  /**
   * Which context entity kind this picker auto-fills from. When set
   * and the matching context entry is present, the picker can
   * pre-populate (gated by the `autoFillFromContext` configForm
   * toggle). Pickers without a sensible auto-fill (e.g. Asset) leave
   * this undefined.
   */
  autoFillFrom?: ContextEntityKind;
  /**
   * Which context entity kinds this picker can filter its query by.
   * Each kind surfaces a `filterBy<X>Context` configForm toggle; when
   * the designer enables it and the matching context entry is set at
   * fill-time, the picker forwards the entity's id as the matching
   * `<x>Oid` arg.
   */
  filterableBy?: ContextEntityKind[];
  /**
   * Filters fixed in the element's own settings at design time (not from
   * context), as extra picker-context args. The Asset element's asset class
   * filter uses it.
   */
  designFilters?: (data: Record<string, unknown> | null | undefined) => Record<string, unknown>;
}

const toPositiveInt = (v: unknown): number | null => {
  if (v === '' || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : null;
};

const isRichEntry = (v: unknown): v is EntityRichEntry =>
  v !== null &&
  typeof v === 'object' &&
  !Array.isArray(v) &&
  typeof (v as { id?: unknown }).id === 'string' &&
  typeof (v as { name?: unknown }).name === 'string';

const toRichEntries = (raw: unknown): EntityRichEntry[] => {
  if (!Array.isArray(raw)) return [];
  return raw.filter(isRichEntry);
};

const defaultSnapshot = (row: EntityPickerRow): EntityRichEntry => ({
  id: row.id,
  name: row.name,
});

/** First letters of the first two words, uppercased — for avatar badges. */
const initials = (name: string): string =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w.charAt(0).toUpperCase())
    .join('') || '?';

const DEFAULT_BADGE_COLOUR = 'var(--forms-accent, var(--forms-accent-default))';

const EntityPicker: React.FC<EntityPickerProps> = ({
  element,
  query,
  collectionField,
  icon,
  iconColour = DEFAULT_BADGE_COLOUR,
  variant = 'icon',
  defaults,
  rowSubtitle,
  rowBadges,
  rowToSnapshot = defaultSnapshot,
  contextSpec,
}) => {
  const [rawValue, setRawValue] = useElementValue<unknown>(element.id);
  const isEditor = useIsEditor();
  const isReadOnly = useIsReadOnly();
  const { getAtPath } = useDocument();
  const { formOid, publicSlug, context: fillContext } = useFormFillContext();

  const data = element.data as {
    label?: string;
    subLabel?: string;
    placeholder?: string;
    helpText?: string;
    required?: boolean;
    hidden?: boolean;
    allowMultiple?: boolean;
    minimum?: number | string;
    maximum?: number | string;
    /**
     * Context-driven configForm flags — populated by the migration
     * that injects toggles into entity pickers. All optional with
     * sensible defaults (off) so legacy element data continues to
     * render without surprises.
     */
    autoFillFromContext?: boolean;
    lockWhenContextFilled?: boolean;
    [filterFlag: string]: unknown;
  };
  const label = data?.label || defaults.label;
  const subLabel = data?.subLabel;
  const placeholder = data?.placeholder || defaults.placeholder;
  const required = !!data?.required;
  const helpText = data?.helpText;
  const allowMultiple = !!data?.allowMultiple;
  const minimum =
    allowMultiple && required ? toPositiveInt(data?.minimum) : null;
  const maximum = allowMultiple ? toPositiveInt(data?.maximum) : null;

  // --- Context wiring ---------------------------------------------
  // The matching context entity for auto-fill, when both the picker
  // declared an `autoFillFrom` and the configForm flag is on.
  const autoFillKind = contextSpec?.autoFillFrom ?? null;
  const autoFillEnabled = autoFillKind !== null && !!data?.autoFillFromContext;
  const autoFillSource =
    autoFillEnabled && autoFillKind ? fillContext[autoFillKind] : null;
  const lockOnContext = autoFillEnabled && !!data?.lockWhenContextFilled;

  // Build per-element filter oids by resolving each filterable kind's
  // configured source. Per-kind shape on element.data:
  //   filterBy<X>          — toggle, top-level on/off
  //   <x>FilterSource      — radio-cards: 'context' | 'element' | 'manual'
  //   <x>FilterElementId   — form-element-picker rich entry [{id, …}]
  //   fixed<X>             — entity-picker rich entry [{id, …}]
  // Forwarded as `<x>Oid` on the GraphQL context input, identical to
  // the old `filterBy<X>Context` flow on the wire — only the source
  // changes.
  const filterOidArgs = useMemo<Record<string, unknown>>(
    () => pickerFilterArgs(contextSpec, data, fillContext, getAtPath),
    [contextSpec, data, fillContext, getAtPath],
  );

  const richEntries = useMemo(() => toRichEntries(rawValue), [rawValue]);
  const selectedIds = useMemo(
    () => new Set(richEntries.map((e) => e.id)),
    [richEntries],
  );

  // Auto-fill effect — when enabled and a matching context entity is
  // set, seed the picker. We only seed when the picker has no value
  // (so a manual override stays put) OR the previously-seeded source
  // changed (re-seed for the new context entity).
  const lastSeededIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (isEditor) return;
    if (!autoFillEnabled || !autoFillSource) return;
    const seedId = autoFillSource.id;
    const currentSeed = lastSeededIdRef.current;
    // Already seeded with the same source; nothing to do.
    if (currentSeed === seedId && richEntries.some((e) => e.id === seedId)) {
      return;
    }
    // If the field has manual picks AND we've never seeded — leave
    // it alone. The filler's input wins.
    if (currentSeed === null && richEntries.length > 0) {
      return;
    }
    setRawValue([{ ...autoFillSource }]);
    lastSeededIdRef.current = seedId;
    // We deliberately don't depend on `richEntries` so that a manual
    // edit AFTER an auto-seed doesn't re-trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFillEnabled, autoFillSource?.id, isEditor]);

  const lockedByContext =
    lockOnContext &&
    !!autoFillSource &&
    richEntries.some((e) => e.id === autoFillSource.id);

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 220);
    return () => clearTimeout(t);
  }, [search]);

  const hasFormContext = !!(formOid || publicSlug);

  const { data: queryData, loading } = useQuery(query, {
    variables: {
      context: {
        formOid: formOid ?? null,
        publicSlug: publicSlug ?? null,
        ...filterOidArgs,
      },
      search: debouncedSearch || null,
      pagination: { page: 1, limit: 25 },
    },
    skip: !open || !hasFormContext,
    fetchPolicy: 'cache-and-network',
  });
  const rows: EntityPickerRow[] =
    (queryData as Record<string, { edges?: EntityPickerRow[] }>)?.[
      collectionField
    ]?.edges ?? [];

  const inputValue = open ? search : '';
  const selectedEntry = !allowMultiple ? richEntries[0] : undefined;
  const selectedSubtitle = selectedEntry
    ? rowSubtitle?.(selectedEntry as EntityPickerRow) ?? null
    : null;

  const pickRow = (row: EntityPickerRow) => {
    const snapshot = rowToSnapshot(row);
    if (allowMultiple) {
      if (selectedIds.has(snapshot.id)) {
        setRawValue(richEntries.filter((e) => e.id !== snapshot.id));
        inputRef.current?.focus();
        return;
      }
      if (maximum !== null && richEntries.length >= maximum) {
        inputRef.current?.focus();
        return;
      }
      setRawValue([...richEntries, snapshot]);
      setSearch('');
      inputRef.current?.focus();
      return;
    }
    setRawValue([snapshot]);
    setOpen(false);
    inputRef.current?.blur();
  };

  const removeEntry = (id: string) => {
    setRawValue(richEntries.filter((e) => e.id !== id));
  };

  const clearAll = () => {
    setRawValue([]);
    setSearch('');
    inputRef.current?.focus();
  };

  const pillCount = richEntries.length;
  const showPill = allowMultiple && (minimum !== null || maximum !== null);
  const belowMin = minimum !== null && pillCount < minimum;
  const atMax = maximum !== null && pillCount >= maximum;
  const pillTarget =
    minimum !== null && maximum !== null
      ? minimum === maximum
        ? `${minimum}`
        : `${minimum}–${maximum}`
      : minimum !== null
      ? `≥${minimum}`
      : `≤${maximum}`;

  // Read-only (e.g. a completed result): render the picked entity /
  // entities as static badges — no search box, no clear/remove
  // buttons, no popover trigger.
  if (isReadOnly) {
    return (
      <div className="space-y-2">
        <div className="space-y-1">
          <FieldLabel htmlFor={element.id} required={required}>
            {label}
          </FieldLabel>
          <FieldSubLabel>{subLabel}</FieldSubLabel>
        </div>
        {richEntries.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {richEntries.map((e) => (
              <Badge
                key={e.id}
                variant="secondary"
                className="flex items-center gap-1 px-2 py-0.5 text-xs font-normal"
              >
                <FontAwesomeIcon icon={icon} className="h-2.5 w-2.5" />
                <span className="max-w-[16rem] truncate">{e.name}</span>
              </Badge>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">—</p>
        )}
        <FieldHelpText>{helpText}</FieldHelpText>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="space-y-1">
        <div className="flex items-center justify-between gap-2">
          <FieldLabel htmlFor={element.id} required={required}>
            {label}
          </FieldLabel>
          {showPill && (
            <span
              className={cn(
                'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium leading-none',
                belowMin
                  ? 'border-destructive bg-destructive/10 text-destructive'
                  : atMax
                  ? 'border-amber-500 bg-amber-50 text-amber-700'
                  : 'border-border bg-muted/40 text-muted-foreground',
              )}
            >
              {pillCount} / {pillTarget}
            </span>
          )}
        </div>
        <FieldSubLabel>{subLabel}</FieldSubLabel>
      </div>

      <Popover
        open={open}
        onOpenChange={(next) => {
          if (!hasFormContext) return;
          if (lockedByContext) return;
          setOpen(next);
        }}
      >
        <PopoverAnchor asChild>
          {allowMultiple ? (
            <div
              className={cn(
                'relative flex min-h-9 flex-wrap items-center gap-1 rounded-md border border-input bg-background px-2 py-1 pr-16 text-sm shadow-sm focus-within:outline-none focus-within:ring-1 focus-within:ring-ring',
                lockedByContext ? 'cursor-default bg-muted/40' : 'cursor-text',
              )}
              onClick={() => !lockedByContext && inputRef.current?.focus()}
            >
              {richEntries.map((e) => (
                <Badge
                  key={e.id}
                  variant="secondary"
                  className="flex items-center gap-1 px-2 py-0.5 text-xs"
                >
                  <FontAwesomeIcon icon={icon} className="h-2.5 w-2.5" />
                  <span className="max-w-[12rem] truncate">{e.name}</span>
                  {!lockedByContext && (
                    <button
                      type="button"
                      onClick={(ev) => {
                        ev.stopPropagation();
                        removeEntry(e.id);
                      }}
                      className="ml-0.5 rounded-full p-0.5 text-muted-foreground hover:text-foreground"
                      aria-label={`Remove ${e.name}`}
                    >
                      <FontAwesomeIcon icon={faXmark} className="h-2.5 w-2.5" />
                    </button>
                  )}
                </Badge>
              ))}
              {!lockedByContext && (
                <Input
                  ref={inputRef}
                  id={element.id}
                  value={inputValue}
                  onChange={(ev) => {
                    setSearch(ev.target.value);
                    if (!open) setOpen(true);
                  }}
                  onFocus={() => hasFormContext && setOpen(true)}
                  placeholder={
                    richEntries.length === 0 ? placeholder : undefined
                  }
                  disabled={!hasFormContext}
                  className="h-7 min-w-[8rem] flex-1 border-0 bg-transparent px-1 py-0 text-sm shadow-none focus-visible:ring-0"
                />
              )}
              <div className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-1 text-muted-foreground">
                {richEntries.length > 0 && !lockedByContext && (
                  <button
                    type="button"
                    onClick={(ev) => {
                      ev.stopPropagation();
                      clearAll();
                    }}
                    className="rounded p-1 hover:bg-muted"
                    aria-label="Clear all picks"
                  >
                    <FontAwesomeIcon icon={faXmark} className="h-3 w-3" />
                  </button>
                )}
                {!lockedByContext && (
                  <FontAwesomeIcon icon={faChevronDown} className="h-3 w-3" />
                )}
              </div>
            </div>
          ) : (
            <div className="w-full">
              {selectedEntry ? (
                // Selected — rich row on the filled tint (library spec).
                <div className="flex w-full items-center gap-[11px] rounded-[var(--forms-input-radius)] border-[length:var(--forms-input-border-w)] border-[var(--forms-input-filled-border)] bg-[var(--forms-input-filled-bg)] px-[12px] py-[9px]">
                  <span
                    className="flex h-[34px] w-[34px] flex-none items-center justify-center text-white"
                    style={{
                      backgroundColor: iconColour,
                      borderRadius: variant === 'avatar' ? '9999px' : '9px',
                    }}
                  >
                    {variant === 'avatar' ? (
                      <span className="text-[12px] font-extrabold">
                        {initials(selectedEntry.name)}
                      </span>
                    ) : (
                      <FontAwesomeIcon icon={icon} style={{ fontSize: 17 }} />
                    )}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[13.5px] font-bold text-[var(--forms-ink)]">
                      {selectedEntry.name}
                    </div>
                    {selectedSubtitle && (
                      <div className="truncate text-[11.5px] text-[var(--forms-muted-soft)]">
                        {selectedSubtitle}
                      </div>
                    )}
                  </div>
                  {!lockedByContext && (
                    <button
                      type="button"
                      onClick={clearAll}
                      aria-label="Clear pick"
                      className="flex-none text-[var(--forms-optional)] transition-colors hover:text-[var(--forms-ink)]"
                    >
                      <FontAwesomeIcon icon={faXmark} style={{ fontSize: 16 }} />
                    </button>
                  )}
                </div>
              ) : (
                // Empty — icon badge + inline search input + search icon.
                <div
                  className={cn(
                    'flex w-full items-center gap-[10px] overflow-hidden rounded-[var(--forms-input-radius)] border-[length:var(--forms-input-border-w)] border-[var(--forms-border)] bg-white px-[13px] transition-[border-color,box-shadow] focus-within:border-[var(--forms-accent,var(--forms-accent-default))] focus-within:shadow-[0_0_0_3px_var(--forms-accent-ring,var(--forms-accent-ring-default))]',
                    lockedByContext && 'bg-muted/40',
                  )}
                  onClick={() =>
                    !lockedByContext && inputRef.current?.focus()
                  }
                >
                  <span
                    className="flex h-[26px] w-[26px] flex-none items-center justify-center rounded-[7px]"
                    style={{
                      backgroundColor: `color-mix(in srgb, ${iconColour} 14%, #fff)`,
                      color: iconColour,
                    }}
                  >
                    <FontAwesomeIcon icon={icon} style={{ fontSize: 15 }} />
                  </span>
                  <input
                    ref={inputRef}
                    id={element.id}
                    value={inputValue}
                    onChange={(ev) => {
                      if (lockedByContext) return;
                      setSearch(ev.target.value);
                      if (!open) setOpen(true);
                    }}
                    onFocus={() =>
                      !lockedByContext && hasFormContext && setOpen(true)
                    }
                    placeholder={placeholder}
                    disabled={!hasFormContext || lockedByContext}
                    className="min-w-0 flex-1 bg-transparent py-[11px] text-[length:var(--forms-input-font)] leading-[1.2] text-[var(--forms-ink)] placeholder:text-[var(--forms-faint)] focus:outline-none"
                  />
                  <FontAwesomeIcon
                    icon={faMagnifyingGlass}
                    className="flex-none text-[var(--forms-optional)]"
                    style={{ fontSize: 15 }}
                  />
                </div>
              )}
            </div>
          )}
        </PopoverAnchor>
        <PopoverContent
          align="start"
          sideOffset={4}
          className="w-[var(--radix-popover-trigger-width)] p-0"
          onOpenAutoFocus={(e) => e.preventDefault()}
          onInteractOutside={(e) => {
            if (e.target === inputRef.current) e.preventDefault();
          }}
          onFocusOutside={(e) => {
            if (e.target === inputRef.current) e.preventDefault();
          }}
        >
          <div className="max-h-72 overflow-auto py-1">
            {loading && rows.length === 0 ? (
              <p className="px-3 py-3 text-center text-xs text-muted-foreground">
                Loading…
              </p>
            ) : rows.length === 0 ? (
              <p className="px-3 py-3 text-center text-xs text-muted-foreground">
                {debouncedSearch
                  ? `No matches for "${debouncedSearch}"`
                  : 'No results.'}
              </p>
            ) : (
              rows.map((row) => {
                const isSelected = selectedIds.has(row.id);
                const subtitle = rowSubtitle?.(row) ?? null;
                const badges =
                  rowBadges?.(row).filter((b) => b && b.trim() !== '') ?? [];
                return (
                  <button
                    key={row.id}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => pickRow(row)}
                    className={cn(
                      'flex w-full items-start gap-3 px-3 py-2 text-left text-sm hover:bg-accent/40',
                      isSelected && 'bg-accent/30',
                    )}
                  >
                    <FontAwesomeIcon
                      icon={icon}
                      className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground"
                    />
                    <div className="flex min-w-0 flex-1 flex-col">
                      <span className="truncate font-medium">{row.name}</span>
                      {subtitle && (
                        <span className="truncate text-xs text-muted-foreground">
                          {subtitle}
                        </span>
                      )}
                      {badges.length > 0 && (
                        <span className="mt-0.5 flex items-center gap-1.5 text-[10px] text-muted-foreground">
                          {badges.map((b, i) => (
                            <span key={`${row.id}-b-${i}`}>
                              {i > 0 && <span className="mr-1.5">·</span>}
                              {b}
                            </span>
                          ))}
                        </span>
                      )}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </PopoverContent>
      </Popover>

      {!hasFormContext && (
        <p className="text-xs text-muted-foreground">
          {isEditor
            ? 'Options will load when filling the form.'
            : 'Form context unavailable — refresh the page.'}
        </p>
      )}
      <FieldHelpText>{helpText}</FieldHelpText>
      <HiddenElementBanner hidden={data?.hidden} />
      <ArchivedElementBanner archived={element.archived} />
    </div>
  );
};

/**
 * Capitalise the first character of an entity-kind so we can compose
 * `filterByCustomer` / `fixedManagedProperty` / `customerOid` style
 * names from a single source.
 */
const ucfirst = (s: string): string =>
  s.length === 0 ? s : s.charAt(0).toUpperCase() + s.slice(1);

/** ConfigForm key names per filterable kind. */
const filterEnableKey = (kind: ContextEntityKind): string =>
  `filterBy${ucfirst(kind)}`;
const filterSourceKey = (kind: ContextEntityKind): string =>
  `${kind}FilterSource`;
const filterElementIdKey = (kind: ContextEntityKind): string =>
  `${kind}FilterElementId`;
const fixedKey = (kind: ContextEntityKind): string => `fixed${ucfirst(kind)}`;

/** Server-side input field name for a context entity oid. */
export const oidKeyFor = (kind: ContextEntityKind): string => `${kind}Oid`;

/**
 * Project a radio-cards-stored value (`[{value, label, description}]`)
 * down to the bare picked string. Returns null when the field is
 * unset / shaped unexpectedly so callers can fall back cleanly.
 */
const readRadioValue = (raw: unknown): string | null => {
  if (!Array.isArray(raw) || raw.length === 0) return null;
  const first = raw[0];
  if (first === null || typeof first !== 'object') return null;
  const v = (first as { value?: unknown }).value;
  return typeof v === 'string' ? v : null;
};

export type FillContext = ReturnType<typeof useFormFillContext>['context'];
export type GetAtPath = ReturnType<typeof useDocument>['getAtPath'];

/**
 * Resolve a single filter kind's oid based on its configured source.
 * Returns null when the filter is off or the source can't yield a
 * useful oid (e.g. element source with no picked source element, or
 * context source with an unset context entry). Callers omit unset
 * oids from the wire payload so the server applies no extra WHERE.
 */
export const resolveFilterOid = (
  kind: ContextEntityKind,
  data: Record<string, unknown> | null | undefined,
  fillContext: FillContext,
  getAtPath: GetAtPath,
): string | null => {
  if (!data) return null;
  if (!data[filterEnableKey(kind)]) return null;
  const source = readRadioValue(data[filterSourceKey(kind)]) ?? 'context';
  if (source === 'manual') {
    const fixed = data[fixedKey(kind)];
    if (Array.isArray(fixed) && fixed[0]) {
      const id = (fixed[0] as { id?: unknown }).id;
      return typeof id === 'string' ? id : null;
    }
    return null;
  }
  if (source === 'element') {
    const elIdEntry = data[filterElementIdKey(kind)];
    let sourceElementId: string | null = null;
    if (Array.isArray(elIdEntry) && elIdEntry[0]) {
      const id = (elIdEntry[0] as { id?: unknown }).id;
      sourceElementId = typeof id === 'string' ? id : null;
    }
    if (!sourceElementId) return null;
    const raw = getAtPath([sourceElementId]);
    const v = unwrapEnvelope(raw);
    if (Array.isArray(v) && v[0]) {
      const id = (v[0] as { id?: unknown }).id;
      return typeof id === 'string' ? id : null;
    }
    return null;
  }
  // context source (default)
  const entry = fillContext[kind];
  return entry?.id ?? null;
};

/**
 * The filter args a picker sends with its query: the `<x>Oid` filters
 * resolved from the element's configured sources, plus any filters fixed in
 * its settings (designFilters). Shared with fill-by-voice so a spoken lookup
 * searches the same rows a tap would.
 */
export const pickerFilterArgs = (
  spec: PickerContextSpec | undefined,
  data: Record<string, unknown> | null | undefined,
  fillContext: FillContext,
  getAtPath: GetAtPath,
): Record<string, unknown> => {
  const out: Record<string, unknown> = {};
  for (const kind of spec?.filterableBy ?? []) {
    const oid = resolveFilterOid(kind, data, fillContext, getAtPath);
    if (oid) out[oidKeyFor(kind)] = oid;
  }
  return { ...out, ...(spec?.designFilters?.(data) ?? {}) };
};

/**
 * Element ids this picker's filters read from other answers (`<x>FilterSource`
 * = "element"), so a caller can know which proposed answers a lookup used.
 */
export const pickerFilterSourceElementIds = (
  spec: PickerContextSpec | undefined,
  data: Record<string, unknown> | null | undefined,
): string[] => {
  const out: string[] = [];
  if (!data) return out;
  for (const kind of spec?.filterableBy ?? []) {
    if (!data[filterEnableKey(kind)]) continue;
    if ((readRadioValue(data[filterSourceKey(kind)]) ?? 'context') !== 'element') continue;
    const entry = data[filterElementIdKey(kind)];
    const id = Array.isArray(entry) && entry[0] ? (entry[0] as { id?: unknown }).id : null;
    if (typeof id === 'string') out.push(id);
  }
  return out;
};

/**
 * True when the picker is showing a context-seeded record it will not let
 * the filler change (`autoFillFromContext` + `lockWhenContextFilled`).
 */
export const pickerLockedByContext = (
  spec: PickerContextSpec | undefined,
  data: Record<string, unknown> | null | undefined,
  fillContext: FillContext,
  rawValue: unknown,
): boolean => {
  const kind = spec?.autoFillFrom;
  if (!kind || !data?.autoFillFromContext || !data?.lockWhenContextFilled) return false;
  const source = fillContext[kind];
  if (!source) return false;
  const entries = toRichEntries(unwrapEnvelope(rawValue));
  // Empty means the picker has not mounted yet (another page) and will seed
  // and lock itself when it does; treat it as locked already.
  return entries.length === 0 || entries.some((e) => e.id === source.id);
};

export default EntityPicker;
